/**
 * The emotional palette of RackSavant.
 *
 * Clothes are not data — they are a mood you can put on. Every garment, every
 * look, and every room in the product is indexed against this taxonomy so the
 * UI can answer the only question that actually matters to our users:
 * "who am I today?"
 */

export const MOOD_IDS = [
  'soft',
  'sharp',
  'feral',
  'clean',
  'dreamy',
  'grunge',
  'regal',
  'sporty',
  'romantic',
  'futuristic',
  'nostalgic',
  'minimal',
  'maximal',
  'moody',
  'sunlit',
] as const;

export type MoodId = (typeof MOOD_IDS)[number];

export interface Mood {
  id: MoodId;
  label: string;
  emoji: string;
  /** Primary hex used for chips, glows and gradient stops. */
  color: string;
  /** Second gradient stop. */
  colorAlt: string;
  /** Shown under the chip in the mood picker. */
  blurb: string;
  /** 1 = still and quiet, 5 = loud and kinetic. Drives motion intensity in the UI. */
  energy: 1 | 2 | 3 | 4 | 5;
  /** -2 cold//clinical .. +2 warm/intimate. Drives lighting hints sent to the image model. */
  warmth: -2 | -1 | 0 | 1 | 2;
  /** Lighting + styling direction handed to the image model for this mood. */
  lightingHint: string;
}

export const MOODS: Record<MoodId, Mood> = {
  soft: {
    id: 'soft',
    label: 'Soft',
    emoji: '🌙',
    color: '#F2B8C6',
    colorAlt: '#FCE1E4',
    blurb: 'gentle, unguarded, cared for',
    energy: 1,
    warmth: 2,
    lightingHint: 'diffused window light, low contrast, gentle skin tones, airy shadows',
  },
  sharp: {
    id: 'sharp',
    label: 'Sharp',
    emoji: '🔪',
    color: '#8C9EFF',
    colorAlt: '#C7D0FF',
    blurb: 'precise, unbothered, in control',
    energy: 3,
    warmth: -1,
    lightingHint: 'hard directional key light, crisp edges, deep defined shadows',
  },
  feral: {
    id: 'feral',
    label: 'Feral',
    emoji: '😈',
    color: '#FF4D6D',
    colorAlt: '#FF9AAE',
    blurb: 'unleashed, a little dangerous',
    energy: 5,
    warmth: 1,
    lightingHint: 'high-contrast flash photography, raw energy, slight motion blur',
  },
  clean: {
    id: 'clean',
    label: 'Clean',
    emoji: '🤍',
    color: '#E8EDF2',
    colorAlt: '#FFFFFF',
    blurb: 'nothing extra, everything intentional',
    energy: 2,
    warmth: 0,
    lightingHint: 'even studio light, seamless backdrop, neutral white balance',
  },
  dreamy: {
    id: 'dreamy',
    label: 'Dreamy',
    emoji: '💭',
    color: '#B79CFF',
    colorAlt: '#E4D9FF',
    blurb: 'half-remembered, floating',
    energy: 2,
    warmth: 1,
    lightingHint: 'hazy bloom, soft halation, pastel colour cast, dreamlike diffusion',
  },
  grunge: {
    id: 'grunge',
    label: 'Grunge',
    emoji: '🕳️',
    color: '#7A8B7F',
    colorAlt: '#B9C4BC',
    blurb: 'worn in, lived in, unbothered',
    energy: 3,
    warmth: -1,
    lightingHint: 'overcast flat light, film grain, desaturated greens and browns',
  },
  regal: {
    id: 'regal',
    label: 'Regal',
    emoji: '👑',
    color: '#F5C518',
    colorAlt: '#FFE79A',
    blurb: 'expensive posture, zero apology',
    energy: 3,
    warmth: 1,
    lightingHint: 'warm golden key light, rich saturation, luxurious depth',
  },
  sporty: {
    id: 'sporty',
    label: 'Sporty',
    emoji: '⚡',
    color: '#4ADE80',
    colorAlt: '#BBF7D0',
    blurb: 'kinetic, capable, awake',
    energy: 5,
    warmth: 0,
    lightingHint: 'bright daylight, high clarity, energetic contrast',
  },
  romantic: {
    id: 'romantic',
    label: 'Romantic',
    emoji: '🌹',
    color: '#FF6FA5',
    colorAlt: '#FFC2D8',
    blurb: 'longing, but make it outerwear',
    energy: 2,
    warmth: 2,
    lightingHint: 'golden hour backlight, warm rim light, soft romantic glow',
  },
  futuristic: {
    id: 'futuristic',
    label: 'Futuristic',
    emoji: '🛸',
    color: '#22D3EE',
    colorAlt: '#A5F3FC',
    blurb: 'from a year that has not happened',
    energy: 4,
    warmth: -2,
    lightingHint: 'cool cyan and magenta gels, glossy highlights, clinical futurism',
  },
  nostalgic: {
    id: 'nostalgic',
    label: 'Nostalgic',
    emoji: '📼',
    color: '#D9A066',
    colorAlt: '#F0D3AE',
    blurb: 'a photo from a summer you miss',
    energy: 2,
    warmth: 2,
    lightingHint: 'warm faded film stock, slight vignette, nostalgic amber cast',
  },
  minimal: {
    id: 'minimal',
    label: 'Minimal',
    emoji: '▫️',
    color: '#A3A3A3',
    colorAlt: '#E5E5E5',
    blurb: 'the loudest thing is the silhouette',
    energy: 1,
    warmth: 0,
    lightingHint: 'single soft source, empty negative space, restrained palette',
  },
  maximal: {
    id: 'maximal',
    label: 'Maximal',
    emoji: '🎠',
    color: '#F97316',
    colorAlt: '#FDBA74',
    blurb: 'more is the whole point',
    energy: 5,
    warmth: 1,
    lightingHint: 'saturated colour, layered texture, busy joyful composition',
  },
  moody: {
    id: 'moody',
    label: 'Moody',
    emoji: '🌑',
    color: '#6366F1',
    colorAlt: '#A5B4FC',
    blurb: 'low light, high feeling',
    energy: 2,
    warmth: -1,
    lightingHint: 'chiaroscuro, deep shadow, single dim practical light source',
  },
  sunlit: {
    id: 'sunlit',
    label: 'Sunlit',
    emoji: '🌞',
    color: '#FBBF24',
    colorAlt: '#FEF08A',
    blurb: 'warm on the shoulders, easy',
    energy: 3,
    warmth: 2,
    lightingHint: 'direct afternoon sun, hard warm shadows, glowing highlights',
  },
};

export const MOOD_LIST: Mood[] = MOOD_IDS.map((id) => MOODS[id]);

export function isMoodId(value: string): value is MoodId {
  return (MOOD_IDS as readonly string[]).includes(value);
}

export function getMood(id: string): Mood | undefined {
  return isMoodId(id) ? MOODS[id] : undefined;
}

/** Average energy of a mood set — drives how animated the result screen feels. */
export function moodEnergy(ids: MoodId[]): number {
  if (ids.length === 0) return 3;
  return ids.reduce((sum, id) => sum + MOODS[id].energy, 0) / ids.length;
}

/**
 * Blend the gradient of a mood set into two stops for backgrounds and glows.
 *
 * Mood colours are content and are allowed to be loud. The no-mood fallback is
 * not a mood, though — it is chrome, so it follows the palette. It used to be
 * the pre-redirection neon, and it shipped on every exported share image for a
 * look that had no mood read.
 */
export function moodGradient(ids: MoodId[]): [string, string] {
  if (ids.length === 0) return ['#7E2436', '#0B0B0C'];
  const first = MOODS[ids[0]];
  const second = ids.length > 1 ? MOODS[ids[1]] : first;
  return [first.color, second.colorAlt];
}

/* ------------------------------------------------------------------ *
 * Hype — reactions, but emotional instead of numeric.
 * A heart tells you nothing. "you look like a menace" tells you everything.
 * ------------------------------------------------------------------ */

export const HYPE_KINDS = [
  'obsessed',
  'unreal',
  'soft_launch',
  'menace',
  'iconic',
  'crying',
  'need_it',
] as const;

export type HypeKind = (typeof HYPE_KINDS)[number];

export interface Hype {
  kind: HypeKind;
  emoji: string;
  label: string;
  /** What it actually says out loud in the feed. */
  phrase: string;
  color: string;
}

export const HYPES: Record<HypeKind, Hype> = {
  obsessed: {
    kind: 'obsessed',
    emoji: '🔥',
    label: 'Obsessed',
    phrase: 'is obsessed with this',
    color: '#FF6B35',
  },
  unreal: {
    kind: 'unreal',
    emoji: '✨',
    label: 'Unreal',
    phrase: 'thinks this is unreal',
    color: '#F5C518',
  },
  soft_launch: {
    kind: 'soft_launch',
    emoji: '🌙',
    label: 'Soft launch',
    phrase: 'says soft launch it',
    color: '#F2B8C6',
  },
  menace: {
    kind: 'menace',
    emoji: '😈',
    label: 'Menace',
    phrase: 'called you a menace',
    color: '#FF4D6D',
  },
  iconic: {
    kind: 'iconic',
    emoji: '👑',
    label: 'Iconic',
    phrase: 'declared this iconic',
    color: '#F5C518',
  },
  crying: {
    kind: 'crying',
    emoji: '😭',
    label: 'Crying',
    phrase: 'is crying about this',
    color: '#8C9EFF',
  },
  need_it: {
    kind: 'need_it',
    emoji: '🫠',
    label: 'Need it',
    phrase: 'needs this immediately',
    color: '#B79CFF',
  },
};

export const HYPE_LIST: Hype[] = HYPE_KINDS.map((k) => HYPES[k]);

export function isHypeKind(value: string): value is HypeKind {
  return (HYPE_KINDS as readonly string[]).includes(value);
}

/* ------------------------------------------------------------------ *
 * Occasions — the "where am I going" axis, orthogonal to mood.
 * ------------------------------------------------------------------ */

export const OCCASION_IDS = [
  'everyday',
  'night_out',
  'work',
  'date',
  'festival',
  'vacation',
  'event',
  'lounge',
] as const;

export type OccasionId = (typeof OCCASION_IDS)[number];

export const OCCASIONS: Record<OccasionId, { id: OccasionId; label: string; emoji: string }> = {
  everyday: { id: 'everyday', label: 'Everyday', emoji: '🚶' },
  night_out: { id: 'night_out', label: 'Night out', emoji: '🍸' },
  work: { id: 'work', label: 'Work', emoji: '💼' },
  date: { id: 'date', label: 'Date', emoji: '💌' },
  festival: { id: 'festival', label: 'Festival', emoji: '🎪' },
  vacation: { id: 'vacation', label: 'Vacation', emoji: '🏝️' },
  event: { id: 'event', label: 'Event', emoji: '🥂' },
  lounge: { id: 'lounge', label: 'Lounge', emoji: '🛋️' },
};

export const OCCASION_LIST = OCCASION_IDS.map((id) => OCCASIONS[id]);

/* ------------------------------------------------------------------ *
 * Garment taxonomy
 * ------------------------------------------------------------------ */

export const GARMENT_CATEGORIES = [
  'tops',
  'bottoms',
  'dresses',
  'outerwear',
  'sets',
  'shoes',
  'accessories',
] as const;

export type GarmentCategory = (typeof GARMENT_CATEGORIES)[number];

export const GARMENT_CATEGORY_LABELS: Record<GarmentCategory, string> = {
  tops: 'Tops',
  bottoms: 'Bottoms',
  dresses: 'Dresses',
  outerwear: 'Outerwear',
  sets: 'Sets',
  shoes: 'Shoes',
  accessories: 'Accessories',
};

/** Which categories can be layered together in one look. */
export const LAYERABLE: Record<GarmentCategory, GarmentCategory[]> = {
  tops: ['bottoms', 'outerwear', 'shoes', 'accessories'],
  bottoms: ['tops', 'outerwear', 'shoes', 'accessories'],
  dresses: ['outerwear', 'shoes', 'accessories'],
  outerwear: ['tops', 'bottoms', 'dresses', 'sets', 'shoes', 'accessories'],
  sets: ['outerwear', 'shoes', 'accessories'],
  shoes: ['tops', 'bottoms', 'dresses', 'sets', 'outerwear', 'accessories'],
  // Accessories are the one category that stacks with itself — a bag, a belt
  // and earrings are three accessories in one look, not a conflict.
  accessories: ['tops', 'bottoms', 'dresses', 'sets', 'outerwear', 'shoes', 'accessories'],
};

/** True when a garment can join an existing selection without conflict. */
export function canLayer(selected: GarmentCategory[], candidate: GarmentCategory): boolean {
  if (selected.length === 0) return true;
  if (selected.includes(candidate) && candidate !== 'accessories') return false;
  return selected.every((s) => LAYERABLE[s].includes(candidate));
}
